import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Gift, Copy, Check, Users, Sparkles, Mail, Headphones } from "lucide-react";
import { toast } from "sonner";

export default function ReferralsPage() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    const { data } = await api.get("/referrals/me");
    setData(data);
  };
  useEffect(() => { load(); }, []);

  if (!data) {
    return <div className="text-[#77849C]">Chargement...</div>;
  }

  const referralLink = `${window.location.origin}/auth?ref=${data.code}`;

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast.success(`${label} copié 📋`);
    } catch {
      toast.error("Copie impossible");
    }
  };

  const shareWhatsapp = () => {
    const msg = encodeURIComponent(
      `Coucou 💛 J'utilise Kynoria, une super appli pour parents. Avec mon code ${data.code}, tu as 3 jours d'essai offerts (sinon il n'y en a pas) : ${referralLink}`
    );
    window.open(`https://wa.me/?text=${msg}`, "_blank");
  };

  const shareEmail = () => {
    const subject = encodeURIComponent("3 jours d'essai offerts sur Kynoria 💛");
    const body = encodeURIComponent(
      `Coucou !\n\nJ'utilise Kynoria, une appli qui aide au quotidien avec les enfants (conseils par âge, contes du soir, coach IA…).\n\nAvec mon code ${data.code}, tu bénéficies de 3 jours d'essai offerts — c'est le seul moyen d'essayer l'app gratuitement 🎁\n\nInscription : ${referralLink}\n\nÀ bientôt !`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const bonusDays = data.bonus_ends_at
    ? Math.max(0, Math.ceil((new Date(data.bonus_ends_at) - new Date()) / 86400000))
    : 0;

  return (
    <div className="space-y-8" data-testid="referrals-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-2xl bg-[#FBE2E9] grid place-items-center">
          <Gift className="h-6 w-6 text-[#E8899F]" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-[#16233D]" style={{ fontFamily: "Outfit, sans-serif" }}>
            Programme parrainage
          </h1>
          <p className="text-[#77849C] text-sm">
            Offre 3 jours d'essai à tes amis · Gagne {data.credits_per_referral} contes audio par ami qui achète 💛
          </p>
        </div>
      </div>

      {/* Hero code card */}
      <section
        className="rounded-3xl p-6 sm:p-8"
        style={{ background: "linear-gradient(135deg, #FBE2E9 0%, #FFF4DC 50%, #E3EAF7 100%)" }}
      >
        <p className="text-xs font-bold uppercase tracking-widest text-[#16233D]">Ton code parrain</p>
        <div className="mt-3 flex items-center justify-between flex-wrap gap-3">
          <div
            data-testid="referral-code"
            className="text-4xl sm:text-5xl font-bold text-[#16233D] tracking-widest"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {data.code}
          </div>
          <button
            data-testid="copy-code-btn"
            onClick={() => copy(data.code, "Code")}
            className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-[#16233D] hover:bg-[#EDF2F9] transition"
          >
            {copied ? <><Check className="h-4 w-4" /> Copié</> : <><Copy className="h-4 w-4" /> Copier</>}
          </button>
        </div>
        <p className="mt-4 text-sm text-[#16233D]">
          Ton lien à partager : <span className="font-medium break-all">{referralLink}</span>
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            data-testid="copy-link-btn"
            onClick={() => copy(referralLink, "Lien")}
            className="rounded-full bg-white px-4 py-2 text-sm font-medium text-[#16233D] hover:bg-[#EDF2F9] transition"
          >
            Copier le lien
          </button>
          <button
            data-testid="share-whatsapp-btn"
            onClick={shareWhatsapp}
            className="rounded-full bg-[#25D366] text-white px-4 py-2 text-sm font-semibold hover:opacity-90 transition"
          >
            WhatsApp
          </button>
          <button
            data-testid="share-email-btn"
            onClick={shareEmail}
            className="rounded-full bg-[#16233D] text-white px-4 py-2 text-sm font-semibold hover:opacity-90 transition inline-flex items-center gap-2"
          >
            <Mail className="h-4 w-4" /> Email
          </button>
        </div>
      </section>

      {/* Stats */}
      <section className="grid sm:grid-cols-3 gap-4">
        <div className="soft-card p-5 text-center" data-testid="stat-referred">
          <Users className="mx-auto h-6 w-6 text-[#23375E]" />
          <p className="mt-2 text-3xl font-bold text-[#16233D]">{data.total_referred}</p>
          <p className="text-sm text-[#77849C]">Amis invités</p>
        </div>
        <div className="soft-card p-5 text-center" data-testid="stat-successful">
          <Sparkles className="mx-auto h-6 w-6 text-[#E8899F]" />
          <p className="mt-2 text-3xl font-bold text-[#16233D]">{data.successful}</p>
          <p className="text-sm text-[#77849C]">Amis qui ont acheté</p>
        </div>
        <div className="soft-card p-5 text-center !bg-[#D2EDE1]" data-testid="stat-audio-credits">
          <Headphones className="mx-auto h-6 w-6 text-[#16233D]" />
          <p className="mt-2 text-3xl font-bold text-[#16233D]">{data.audio_credits}</p>
          <p className="text-sm text-[#16233D]">Contes audio débloqués</p>
          <p className="text-xs text-[#16233D] mt-1">
            +{data.credits_per_referral} par ami qui achète
          </p>
          {bonusDays > 0 && (
            <p className="text-xs text-[#16233D] mt-1">Bonus historique actif : {bonusDays} jours</p>
          )}
        </div>
      </section>

      {/* How it works */}
      <section className="soft-card p-6">
        <h3 className="text-xl font-semibold text-[#16233D]">Comment ça marche ?</h3>
        <ol className="mt-4 grid md:grid-cols-3 gap-4">
          {[
            { n: "1", t: "Partage ton code", d: "Envoie ton code ou ton lien à un ami parent." },
            { n: "2", t: "Il essaie gratuitement", d: "Avec ton code, il obtient 3 jours d'essai — le seul moyen d'essayer Kynoria." },
            { n: "3", t: "Tu gagnes la voix", d: `S'il achète l'accès à vie, tu débloques ${data.credits_per_referral} contes racontés à voix haute 🎧` },
          ].map((s) => (
            <li key={s.n} className="rounded-2xl bg-[#EDF2F9] p-4">
              <div className="h-8 w-8 rounded-full bg-[#23375E] text-white grid place-items-center font-bold">
                {s.n}
              </div>
              <p className="mt-2 font-semibold text-[#16233D]">{s.t}</p>
              <p className="text-sm text-[#44506B]">{s.d}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* Referred list */}
      <section>
        <h3 className="text-xl font-semibold text-[#16233D]">Tes filleuls</h3>
        {data.referred_users.length === 0 && (
          <p className="mt-2 text-[#77849C]">Aucun filleul pour l'instant. Partage ton code !</p>
        )}
        <div className="mt-4 space-y-2">
          {data.referred_users.map((r, i) => (
            <div key={i} data-testid={`referred-${i}`} className="soft-card p-4 flex items-center justify-between">
              <div>
                <p className="font-semibold text-[#16233D]">{r.name}</p>
                <p className="text-xs text-[#77849C]">
                  Invité{" "}
                  {new Date(r.invited_at).toLocaleDateString("fr-FR", { dateStyle: "medium" })}
                </p>
              </div>
              {r.subscribed ? (
                <span className="rounded-full bg-[#D2EDE1] text-[#16233D] px-3 py-1 text-xs font-semibold">
                  A acheté · {data.credits_per_referral} contes audio crédités
                </span>
              ) : (
                <span className="rounded-full bg-[#FFF4DC] text-[#16233D] px-3 py-1 text-xs font-semibold">
                  En attente
                </span>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
